"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import { Bell, MessageSquare } from "lucide-react";
import { Tag } from "@/components/ui/tag";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { getChatRoomDisplayName } from "@/lib/chatDisplay";
import { applyChatRoomActivity, markChatRoomRead } from "@/lib/chatRooms";
import { useRouter } from "@/lib/router-compat";
import { chatRepository } from "@/repository/chatRepository";
import {
  staffNotificationEventRepository,
  StaffNotificationEvent,
} from "@/repository/staffNotificationEventRepository";
import { ChatRoom } from "@/types/chat";
import { getHeaderChatNotificationPreview } from "@/lib/chatInbox";
import {
  STAFF_NOTIFICATION_EVENTS_CHANGED,
  STAFF_NOTIFICATION_REALTIME_CHANGED,
  STAFF_NOTIFICATION_TYPE_BADGE_CLASS,
  STAFF_NOTIFICATION_TYPE_LABELS,
  formatStaffNotificationEventTime,
  notifyStaffNotificationEventsChanged,
} from "@/lib/staff-inbox";
import { resolveStaffNotificationDestination } from "@/lib/staffNotificationDeepLink";
import { useToast } from "@/hooks/use-toast";
import { useChatSocket } from "@/hooks/useChatSocket";

interface NotificationData {
  rooms: ChatRoom[];
  /** 直近の業務通知イベント (既読含む最新5件。未読数は eventsUnreadCount) */
  events: StaffNotificationEvent[];
  eventsUnreadCount: number;
}

const FALLBACK_REFRESH_BASE_MS = 5 * 60_000;
const FALLBACK_REFRESH_JITTER_MS = 30_000;

export function HeaderNotificationPopover() {
  const router = useRouter();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState<NotificationData>({
    rooms: [],
    events: [],
    eventsUnreadCount: 0,
  });
  const roomsRef = useRef<ChatRoom[]>([]);
  const roomsRequestRef = useRef<Promise<void> | null>(null);
  const eventsRequestRef = useRef<Promise<void> | null>(null);
  const eventsRefreshTimerRef = useRef<number | null>(null);
  const notifyRealtimeConsumersRef = useRef(false);
  const hasConnectedOnceRef = useRef(false);
  const { isConnected, onRoomActivity, onStaffInboxChanged } = useChatSocket();

  const fetchRooms = useCallback(async () => {
    if (roomsRequestRef.current) return roomsRequestRef.current;
    const request = (async () => {
      try {
        const rooms = await chatRepository.getRooms();
        roomsRef.current = rooms;
        setData((previous) => ({ ...previous, rooms }));
      } catch {
        // 一時的な通信失敗で取得済みの未読表示を消さない
      }
    })();
    roomsRequestRef.current = request;
    try {
      await request;
    } finally {
      if (roomsRequestRef.current === request) roomsRequestRef.current = null;
    }
  }, []);

  const fetchEvents = useCallback(async () => {
    if (eventsRequestRef.current) return eventsRequestRef.current;
    const request = (async () => {
      try {
        const response = await staffNotificationEventRepository.list({
          limit: 5,
        });
        setData((previous) => ({
          ...previous,
          events: response.items,
          eventsUnreadCount: response.unreadCount,
        }));
      } catch {
        // 一時的な通信失敗で取得済みの未読表示を消さない
      }
    })();
    eventsRequestRef.current = request;
    try {
      await request;
    } finally {
      if (eventsRequestRef.current === request) eventsRequestRef.current = null;
    }
  }, []);

  const fetchAll = useCallback(async () => {
    await Promise.all([fetchRooms(), fetchEvents()]);
  }, [fetchEvents, fetchRooms]);

  const scheduleEventsRefresh = useCallback(
    (notifyConsumers = false) => {
      if (notifyConsumers) notifyRealtimeConsumersRef.current = true;
      if (eventsRefreshTimerRef.current !== null) {
        window.clearTimeout(eventsRefreshTimerRef.current);
      }
      eventsRefreshTimerRef.current = window.setTimeout(() => {
        eventsRefreshTimerRef.current = null;
        void fetchEvents();
        if (notifyRealtimeConsumersRef.current) {
          notifyRealtimeConsumersRef.current = false;
          window.dispatchEvent(new Event(STAFF_NOTIFICATION_REALTIME_CHANGED));
        }
      }, 350);
    },
    [fetchEvents]
  );

  useEffect(() => {
    void fetchAll();

    const handleChatRoomRead = (e: Event) => {
      const roomId = (e as CustomEvent<{ roomId?: string }>).detail?.roomId;
      if (!roomId) {
        void fetchAll();
        return;
      }
      setData((prev) => {
        const rooms = markChatRoomRead(prev.rooms, roomId);
        roomsRef.current = rooms;
        return { ...prev, rooms };
      });
    };
    window.addEventListener("chat:room-read", handleChatRoomRead);

    // 通知一覧ページでの既読 / 未読操作をバッジへ即時反映する
    const handleEventsChanged = () => {
      scheduleEventsRefresh();
    };
    window.addEventListener(
      STAFF_NOTIFICATION_EVENTS_CHANGED,
      handleEventsChanged
    );

    // 別端末 (スタッフモバイル / 親アプリ) で既読化された変更を反映するため、
    // ウィンドウへ戻ってきたタイミング / タブが可視に戻ったタイミングで再フェッチする。
    const handleFocusOrVisible = () => {
      if (document.visibilityState === "visible") {
        void fetchAll();
      }
    };
    window.addEventListener("focus", handleFocusOrVisible);
    document.addEventListener("visibilitychange", handleFocusOrVisible);
    // WebSocketの取りこぼしを回復する保険。全クライアントが同時刻にAPIへ
    // 集中しないよう、5分に最大30秒の揺らぎを加えて次回を予約する。
    let refreshTimer: number | undefined;
    const scheduleFallbackRefresh = () => {
      const delay =
        FALLBACK_REFRESH_BASE_MS +
        Math.floor(Math.random() * FALLBACK_REFRESH_JITTER_MS);
      refreshTimer = window.setTimeout(() => {
        handleFocusOrVisible();
        scheduleFallbackRefresh();
      }, delay);
    };
    scheduleFallbackRefresh();

    return () => {
      window.removeEventListener("chat:room-read", handleChatRoomRead);
      window.removeEventListener(
        STAFF_NOTIFICATION_EVENTS_CHANGED,
        handleEventsChanged
      );
      window.removeEventListener("focus", handleFocusOrVisible);
      document.removeEventListener("visibilitychange", handleFocusOrVisible);
      if (refreshTimer !== undefined) window.clearTimeout(refreshTimer);
      if (eventsRefreshTimerRef.current !== null) {
        window.clearTimeout(eventsRefreshTimerRef.current);
        eventsRefreshTimerRef.current = null;
      }
    };
  }, [fetchAll, scheduleEventsRefresh]);

  useEffect(() => {
    const unsubscribeInbox = onStaffInboxChanged(() => {
      scheduleEventsRefresh(true);
    });
    const unsubscribeRoomActivity = onRoomActivity((activity) => {
      const currentRooms = roomsRef.current;
      const result = applyChatRoomActivity(currentRooms, activity);
      if (!result.found) {
        // 接続後に作られた新規ルームは表示情報を持たないため一度だけ全取得する。
        void fetchRooms();
        return;
      }
      roomsRef.current = result.rooms;
      setData((previous) => ({ ...previous, rooms: result.rooms }));
    });
    return () => {
      unsubscribeInbox();
      unsubscribeRoomActivity();
    };
  }, [fetchRooms, onRoomActivity, onStaffInboxChanged, scheduleEventsRefresh]);

  // 切断中のイベントを回復する。初回接続は初期取得と重なるため再取得しない。
  useEffect(() => {
    if (!isConnected) return;
    if (hasConnectedOnceRef.current) {
      void fetchAll();
      return;
    }
    hasConnectedOnceRef.current = true;
  }, [fetchAll, isConnected]);

  const handleEventClick = (event: StaffNotificationEvent) => {
    setOpen(false);
    // Optimistic: クリックしたイベントを即時に既読表示へ（一覧からは消さない）
    setData((prev) => ({
      ...prev,
      events: prev.events.map((e) =>
        e.id === event.id ? { ...e, isRead: true } : e
      ),
      eventsUnreadCount: event.isRead
        ? prev.eventsUnreadCount
        : Math.max(prev.eventsUnreadCount - 1, 0),
    }));
    if (!event.isRead) {
      void staffNotificationEventRepository
        .markRead(event.id)
        .then(() => notifyStaffNotificationEventsChanged())
        .catch(() => {
          toast({
            title: "通知を既読にできませんでした",
            description: "表示を更新してサーバーの状態に戻しました。",
            variant: "destructive",
          });
          void fetchEvents();
        });
    }
    const destination = resolveStaffNotificationDestination(event);
    if (destination.notice) toast(destination.notice);
    router.push(destination.href);
  };

  const handleRoomClick = (roomId: string) => {
    setOpen(false);
    // Optimistic: クリックしたルームをローカル即時に既読扱い
    setData((prev) => ({
      ...prev,
      rooms: markChatRoomRead(prev.rooms, roomId),
    }));
    roomsRef.current = markChatRoomRead(roomsRef.current, roomId);
    router.push(`/chat/${roomId}`);
  };

  const { rooms, events, eventsUnreadCount } = data;
  const unreadRooms = rooms.filter((room) => room.unreadCount > 0);
  const totalUnread =
    unreadRooms.reduce((sum, r) => sum + r.unreadCount, 0) + eventsUnreadCount;

  return (
    <Popover
      open={open}
      onOpenChange={(next: boolean) => {
        setOpen(next);
        if (next) void fetchAll();
      }}
    >
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="icon"
          aria-label="通知を開く"
          className="hover:text-brand text-muted-foreground relative h-11 w-11 lg:h-10 lg:w-10"
        >
          <Bell className="h-4 w-4" aria-hidden="true" />
          {totalUnread > 0 && (
            <span
              aria-label={`未読 ${totalUnread} 件`}
              className="pointer-events-none absolute -right-1.5 -top-1.5 inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-rose-400 px-1.5 text-[10px] font-semibold tabular-nums leading-none text-white ring-2 ring-white"
            >
              {totalUnread > 99 ? "99+" : totalUnread}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <div className="border-border border-b px-4 py-3">
          <p className="text-foreground text-sm font-semibold">通知</p>
        </div>
        <div className="max-h-[480px] overflow-y-auto">
          {/* 業務通知イベント: 直近5件（既読含む）を並べる。
              1行目 = 未読ドット＋タイトル＋受信日時、
              2行目 = カテゴリバッジ＋内容のチラ見え（開かなくても
              「誰から・何についてか」を直感的に把握できるように）。 */}
          {events.length > 0 && (
            <section className="border-b border-gray-100">
              <div className="bg-muted/50 flex items-center gap-1.5 px-4 py-2">
                <Bell
                  className="text-muted-foreground h-3.5 w-3.5"
                  aria-hidden="true"
                />
                <p className="text-muted-foreground text-xs font-medium">
                  業務通知
                </p>
              </div>
              <ul>
                {events.map((event) => (
                  <li key={event.id}>
                    <button
                      type="button"
                      onClick={() => handleEventClick(event)}
                      className="hover:bg-muted/50 flex w-full flex-col gap-1 px-4 py-3 text-left transition-colors"
                    >
                      <span className="flex w-full items-center gap-2">
                        <span
                          aria-hidden="true"
                          className={`h-2 w-2 shrink-0 rounded-full ${
                            event.isRead ? "bg-transparent" : "bg-primary"
                          }`}
                        />
                        <span
                          className={`min-w-0 flex-1 truncate text-sm ${
                            event.isRead
                              ? "text-muted-foreground"
                              : "text-foreground font-medium"
                          }`}
                        >
                          {event.title}
                        </span>
                        <span className="text-muted-foreground shrink-0 text-xs">
                          {formatStaffNotificationEventTime(event.createdAt)}
                        </span>
                      </span>
                      <span className="flex w-full items-center gap-1.5 pl-4">
                        <Tag
                          tone="neutral"
                          className={`${STAFF_NOTIFICATION_TYPE_BADGE_CLASS[event.type]} shrink-0 px-1.5 py-0 text-[10px] ${
                            event.isRead ? "opacity-60" : ""
                          }`}
                          size="compact"
                        >
                          {STAFF_NOTIFICATION_TYPE_LABELS[event.type]}
                        </Tag>
                        {event.body && (
                          <span className="text-muted-foreground min-w-0 flex-1 truncate text-xs">
                            {event.body}
                          </span>
                        )}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
              <div className="px-4 pb-2">
                <button
                  type="button"
                  aria-label="業務通知をすべて表示"
                  onClick={() => {
                    setOpen(false);
                    router.push("/notifications/inbox");
                  }}
                  className="text-primary cursor-pointer text-xs font-medium hover:underline"
                >
                  すべて表示
                </button>
              </div>
            </section>
          )}

          {/* メッセージセクション */}
          {unreadRooms.length > 0 && (
            <section>
              <div className="bg-muted/50 flex items-center gap-1.5 px-4 py-2">
                <MessageSquare className="text-muted-foreground h-3.5 w-3.5" />
                <p className="text-muted-foreground text-xs font-medium">
                  メッセージ
                </p>
              </div>
              <ul>
                {unreadRooms.slice(0, 5).map((room) => {
                  const roomName = getChatRoomDisplayName(room);
                  return (
                    <li key={room.id}>
                      <button
                        type="button"
                        onClick={() => handleRoomClick(room.id)}
                        className="hover:bg-muted/50 flex w-full items-center justify-between px-4 py-3 text-left transition-colors"
                      >
                        <div className="min-w-0 flex-1">
                          <p className="text-foreground truncate text-sm font-medium">
                            {roomName}
                          </p>
                          {room.lastMessage && (
                            <p className="text-muted-foreground mt-0.5 truncate text-xs">
                              {getHeaderChatNotificationPreview(room)}
                            </p>
                          )}
                        </div>
                        {room.unreadCount > 0 && (
                          <Tag
                            tone="danger"
                            className="ml-2 shrink-0 text-[10px]"
                            size="compact"
                          >
                            {room.unreadCount}
                          </Tag>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
              <div className="border-b border-gray-100 px-4 pb-2">
                <button
                  type="button"
                  aria-label="メッセージをすべて表示"
                  onClick={() => {
                    setOpen(false);
                    router.push("/chat");
                  }}
                  className="text-primary cursor-pointer text-xs font-medium hover:underline"
                >
                  すべて表示
                </button>
              </div>
            </section>
          )}

          {/* 全件0件のとき */}
          {events.length === 0 && unreadRooms.length === 0 && (
            <div className="px-4 py-8 text-center">
              <Bell className="text-muted-foreground/40 mx-auto mb-2 h-8 w-8" />
              <p className="text-muted-foreground text-sm">
                新しい通知はありません
              </p>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
